import {createApp} from "vue";
import {TextureLoader} from "three";
import {GLTFLoader} from "three/addons/loaders/GLTFLoader.js";
import Dash from "./lib/dash";
import Loader from "./lib/loader";
import smooth from "./lib/smooth";
import router from "./lib/router";
import components from "./components";
import transition from "./lib/transition";
import {globalEmitter} from "@straylightagency/events";
import {useRequest, withHeaders} from "@straylightagency/http-client";
import {animate, fadeHide, fadeShow} from "@straylightagency/animate";
import {easeInOutQuad, easeLinear} from "@straylightagency/animate/easing";
import {onEachElements, addEventListeners, getElementSizing,
    unlessElementExists, whenReady, whenResize} from "@straylightagency/utils/dom";
import {throttle, nextTick, milliseconds, seconds} from "@straylightagency/utils/misc";
import scene from './scene.js';
import {loadLocalizedStringsFrom} from "./lib/localization";
import perlin from '../assets/perlin-512.png';
import SplitType from "split-type";

loadLocalizedStringsFrom( window.localizedStrings );

withHeaders({
    'X-CSRF-TOKEN': document.querySelector("meta[name='csrf']").getAttribute('content'),
});

if ('scrollRestoration' in window.history) {
    window.history.scrollRestoration = 'manual';
}

let app;
let container;

let menu_opened = false;
let first_load = true;

const is_prod = process.env.NODE_ENV === 'production';

const wrapper = document.querySelector('.wrapper');

const parser = new DOMParser;
const loader = new Loader;

const gltfLoader = new GLTFLoader;
const textureLoader = new TextureLoader;

const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
mediaQuery.addEventListener('change', () => window.location.reload() );

loader.when('image', (tag, loaded) => {
    const image = new Image();

    const source = tag.getAttribute('data-load-img');

    const cleaner = addEventListeners( image, {
        load() {
            cleaner.load();
            cleaner.error();

            tag.src = image.src;

            loaded( image.src );
        },
        error() {
            cleaner.load();
            cleaner.error();

            console.error( `Error while loading ${source}` );
        }
    } );

    image.src = source;
} );

loader.when('background', (tag, loaded) => {
    const image = new Image();

    const source = tag.getAttribute('data-load-bg');

    const cleaner = addEventListeners( image, {
        load() {
            cleaner.load();
            cleaner.error();

            tag.style.backgroundImage = 'url('+ image.src +')';

            loaded( image.src );
        },
        error() {
            cleaner.load();
            cleaner.error();

            console.error( `Error while loading ${source}` );
        }
    } );

    image.src = source;
} );

loader.when('gltf', async (asset, loaded) => {
    const response = await gltfLoader.loadAsync( asset ).catch( console.error );
    loaded( response );
} );

loader.when('texture', async (asset, loaded) => {
    const response = await textureLoader.loadAsync( asset ).catch( console.error );
    loaded( response );
} );

function toggleMenu() {
    menu_opened = !menu_opened;
    document.body.classList.toggle('menu-opened');
    document.querySelector('.menu').classList.toggle('is-opened');
    document.querySelector('.burger').classList.toggle('is-cross');
}

router.any(async () => {
    unlessElementExists( '.app', containerEl => {
        if ( app ) {
            app.unmount();
            containerEl.innerHTML = container.innerHTML;
        } else {
            container = containerEl.cloneNode(true);
        }

        app = createApp( {
            components,
            mounted: async () => {
                const container = document.querySelector('#transition');
                transition.setWrapper( container );

                unlessElementExists('.nav', nav => {
                    let last_pos_y = 0;
                    let in_transition = false;

                    nav.addEventListener('transitionstart', () => {
                        in_transition = true;

                        setTimeout( () => in_transition = false, 300 );
                    } );

                    const headerScroll = pos_y => {
                        if ( !menu_opened ) {
                            if ( pos_y >= 800 ) {
                                if ( pos_y > last_pos_y ) {
                                    nav.classList.add('is-hidden');
                                } else {
                                    if ( in_transition ) {
                                        return;
                                    }

                                    nav.classList.remove('is-hidden');
                                }
                            } else {
                                nav.classList.remove('is-hidden');
                            }
                        }

                        if ( pos_y <= 50 ) {
                            nav.classList.remove('is-sticky');
                        } else {
                            nav.classList.add('is-sticky');
                        }

                        last_pos_y = pos_y;
                    };

                    window.addEventListener('scroll', throttle( () => headerScroll( window.scrollY ), 500 ) );
                    // smooth.add( ({deltaY}) => headerScroll(deltaY * -1) );
                    headerScroll( 0 );
                } );

                onEachElements( '.nav-dropdown', dropdown => {
                    const list = dropdown.querySelector('.nav-dropdown-wrapper');

                    addEventListeners( dropdown, {
                        mouseenter() {
                            fadeShow( list, 200 );
                        },
                        mouseleave() {
                            fadeHide( list, 200 );
                        },
                    } );
                }  );

                unlessElementExists('#openMenu', button => button.addEventListener('click', toggleMenu ) );

                unlessElementExists('#showCookiesNotice', button => button.addEventListener('click', () => globalEmitter.emit('showCookiesNotice') ) );

                if ( first_load ) {
                    await fadeShow( container, 10 );
                    await fadeShow( wrapper , 500 );
                } else {
                    await fadeShow( container, 300 );
                }

                onEachElements( '[data-dash]', el => {
                    let split;

                    if ( el.getAttribute('data-dash') === 'white-up' ) {
                        whenResize(() => {
                            split && split.revert( el );

                            split = new SplitType( el, {
                                types: "lines, words",
                                tagName: "div"
                            } );
                        });
                    }

                    new Dash( el, {
                        onShow: el => el.classList.add( el.getAttribute('data-dash-class') || 'dash-animated' ),
                        onHide: el => el.classList.remove( el.getAttribute('data-dash-class') || 'dash-animated' )
                    } );
                } );

                let sameHeightsValues = {};
                let sameHeights = document.querySelectorAll("[data-same-height]");

                const computeHeights = () => {
                    sameHeightsValues = {};

                    sameHeights.forEach( el => {
                        el.style.removeProperty('height');

                        const group = el.getAttribute('data-same-height');

                        if ( !sameHeightsValues[ group ] ) {
                            sameHeightsValues[ group ] = 0;
                        }

                        let height = el.offsetHeight;

                        if ( height > sameHeightsValues[ group ] ) {
                            sameHeightsValues[ group ] = height;
                        }
                    } );

                    sameHeights.forEach( el => {
                        const group = el.getAttribute('data-same-height');
                        const minWidth = el.getAttribute('data-same-height-min-width') || 0;

                        if ( parseInt( minWidth ) <= window.innerWidth ) {
                            el.style.height = sameHeightsValues[ group ] + "px";
                        }
                    } );
                };

                whenResize( computeHeights );

                await milliseconds( 100 );
            }
        } );

        app.mount( containerEl );
    } );
} );

router.when('\/(nos-projets|our-projects)\/*$', async () => {
    const header = document.querySelector('.project-header');
    const height = 80;

    await milliseconds( 300 );

    await animate( {
        from: 100,
        to: height,
        duration: 500,
        easing: easeInOutQuad,
        update: value => header.style.height = ( Math.max( height, value ) * ( window.innerHeight / 100 ) ) + "px",
    } );
} );

transition.onChange( async ({links, url, wrapper}) => {
    container.querySelector('#transition').innerHTML = wrapper.innerHTML;

    router.handle( url.pathname );

    links.forEach( linkElement => {
        linkElement.classList.remove('active');

        const linkUrl = new URL( linkElement.href, url );

        if ( linkUrl.href === url.href ||
            ( linkUrl.pathname !== '/' && url.href.includes( linkUrl.href ) ) )
        {
            linkElement.classList.add('active');
        }
    } );
} );

transition.register({
    async fetch(url) {
        const html = await useRequest( url ).get();

        return parser.parseFromString( html.body, "text/html");
    },

    transitions: {
        default: {
            async onLeave({wrapper}) {
                menu_opened && toggleMenu();

                await fadeHide( wrapper, 300 );

                await nextTick( () => window.scrollTo( 0, 0 ) );

                await milliseconds( 100 );
            },

            async onEnter({wrapper}) {
                document.querySelectorAll("[data-load-bg]").forEach( item => loader.push('background', item ) );
                document.querySelectorAll('[data-load-img]').forEach( item => loader.push('image', item ) );

                await nextTick( () => window.scrollTo( 0, 0 ) );

                await loader.handle( {
                    finished: async () => {
                        await fadeShow( wrapper, 300 );

                        await milliseconds( 100 );
                    }
                } );

                await milliseconds( 100 );
            }
        },

        project: {
            async onLeave({wrapper, link}) {
                menu_opened && toggleMenu();
                document.body.classList.add('overflow-hidden');

                if ( link ) {
                    const img = link.querySelector('.project-thumbnail');
                    const clone = img.cloneNode(true);
                    const sizing = getElementSizing( img );
                    const imgWrapper = document.querySelector('.project-thumbnail-wrapper');

                    const from = {
                        top: sizing.top,
                        left: sizing.left,
                        bottom: (window.innerHeight - ( sizing.top + sizing.height )),
                        right: (window.innerWidth - ( sizing.left + sizing.width )),
                    };

                    imgWrapper.style.top = from.top + "px";
                    imgWrapper.style.left = from.left + "px";

                    imgWrapper.style.bottom = from.bottom + "px";
                    imgWrapper.style.right = from.right + "px";

                    imgWrapper.appendChild( clone );

                    await animate( {
                        from,
                        to: {
                            top: 0,
                            left: 0,
                            bottom: 0,
                            right: 0,
                        },
                        duration: 500,
                        easing: easeInOutQuad,
                        update: values => {
                            imgWrapper.style.top = Math.max( 0, values.top ) + "px";
                            imgWrapper.style.left = Math.max( 0, values.left ) + "px";
                            imgWrapper.style.bottom = Math.max( 0, values.bottom ) + "px";
                            imgWrapper.style.right = Math.max( 0, values.right ) + "px";
                        },
                    } );
                } else {
                    await fadeHide( wrapper, 300 );
                }

                await nextTick( () => window.scrollTo( 0, 0 ) );

                await milliseconds( 100 );

                document.body.classList.remove('overflow-hidden');
            },

            async onEnter({wrapper, link}) {
                document.body.classList.add('overflow-hidden');

                document.querySelectorAll("[data-load-bg]").forEach( item => loader.push('background', item ) );
                document.querySelectorAll('[data-load-img]').forEach( item => loader.push('image', item ) );

                await nextTick( () => window.scrollTo( 0, 0 ) );

                const imgWrapper = document.querySelector('.project-thumbnail-wrapper');

                await loader.handle( {
                    finished: async () => {
                        if ( link ) {
                            await fadeHide(imgWrapper, 300);

                            imgWrapper.innerHTML = '';
                            imgWrapper.attributes.removeNamedItem('style');
                        } else {
                            await nextTick( () => window.scrollTo( 0, 0 ) );

                            await fadeShow( wrapper, 300 );

                            await milliseconds( 300 );
                        }
                    }
                } );

                document.body.classList.remove('overflow-hidden');
            }
        },
    },

    ignoreClasses: ['no-anim'],
});

smooth.register();

whenReady( async () => {
    document.body.classList.add('overflow-hidden');
    const show_animation = is_prod && document.body.classList.contains('home');

    const loading = document.querySelector('.loading');
    const percentage = document.querySelector('.loading-percentage');
    const sentence = document.querySelector('.loading-sentence');
    const lines = document.querySelectorAll('.bg-line');
    const canvas = document.querySelector('.canvas');

    document.querySelectorAll("[data-load-bg]").forEach( item => loader.push('background', item ) );
    document.querySelectorAll('[data-load-img]').forEach( item => loader.push('image', item ) );

    loader.push( 'texture', perlin, 'perlin' );
    loader.push( 'gltf', '/assets/triangles.glb', 'triangles' );

    await loader.handle( {
        progress: ({count, max}) => percentage.querySelector('span').innerHTML = Math.ceil((count * 100) / max),
        finished: async ({items}) => {
            if ( await scene( canvas, {
                texture: items.find( ({name}) => name === 'perlin' )?.asset,
                triangles: items.find( ({name}) => name === 'triangles' )?.asset,
            }, false ) ) {
                document.body.classList.remove('no-anim');
            }

            if ( show_animation ) {
                await fadeShow( sentence, 500 );

                await seconds( 2 );

                await fadeHide( percentage, 500 );

                await seconds( 2 );

                await fadeHide( sentence, 500 );
            } else {
                await fadeHide( percentage, 500 );
            }

            if ( is_prod ) {
                await seconds( 1 );
            }

            const maxHeight = window.screen.height;

            await animate( {
                from: 0,
                to: maxHeight,
                duration: show_animation ? 750 : ( is_prod ? 450 : 2000),
                easing: easeLinear,
                update: value => {
                    if ( value >= maxHeight ) {
                        value = maxHeight;
                    }

                    lines.forEach( line => {
                        line.style.height = (value < 0 ? 0 : value) + "px"
                    } );
                },
            } );

            await milliseconds( 500 );

            await fadeShow( canvas , 500 );

            router.handle();

            loading.classList.add('hidden', 'pointer-events-none');
            document.body.classList.remove('overflow-hidden');

            first_load = false;
        }
    } );
} );